import React, { ReactNode } from 'react';
import HeadingHighlight from '@/components/HeadingHighlight';

interface ResponsiveHeadingProps {
  children: ReactNode;
  subtext?: string;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  centered?: boolean;
  withHighlight?: boolean;
  id?: string;
}

const sizeClasses = {
  sm: 'text-xl sm:text-2xl md:text-2xl',
  md: 'text-2xl sm:text-2xl md:text-3xl',
  lg: 'text-2xl sm:text-3xl md:text-4xl',
  xl: 'text-3xl sm:text-3xl md:text-4xl lg:text-5xl',
  '2xl': 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl',
  '3xl': 'text-4xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl',
  '4xl': 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl',
  '5xl': 'text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl',
};

const ResponsiveHeading: React.FC<ResponsiveHeadingProps> = ({
  children,
  subtext,
  className = '',
  as = 'h2',
  size = 'lg',
  centered = false,
  withHighlight = true,
  id,
}) => {
  const combinedClassName = `
    font-bold tracking-tight
    ${sizeClasses[size]}
    ${centered ? 'text-center' : ''}
    ${className}
  `.trim();

  const content = withHighlight ? (
    <HeadingHighlight as={as} size={size} className={combinedClassName} id={id}>
      {children}
    </HeadingHighlight>
  ) : (
    React.createElement(as, { className: combinedClassName, id }, children)
  );

  return (
    <div className={`${centered ? 'text-center' : ''} mb-4 md:mb-6`}>
      {content}
      {subtext && (
        <p className="mt-2 md:mt-4 text-lg sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
          {subtext}
        </p>
      )}
    </div>
  );
};

export default ResponsiveHeading; 